import {useRef, useState} from 'react';

import {useRedirect} from '@/components/global/providers/RedirectionProvider';
import {useSignOutUserMutation} from '@/graphql/types';
import {ROOT_PATH} from '@/paths';
import compilePath from '@/utils/compilePath';

type SignOutOptions = {
  // Lands here instead of going through the Auth0 logout
  to?: string;
};

const useSignOut = () => {
  const redirect = useRedirect();
  const [signOutUser, {client}] = useSignOutUserMutation();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const hasStarted = useRef(false);

  const signOut = async ({to}: SignOutOptions = {}) => {
    // Never reset: the page is about to unload, and a second sign-out would cancel the
    // in-flight Auth0 logout redirect
    if (hasStarted.current) return;
    hasStarted.current = true;
    setIsSigningOut(true);

    let auth0LogoutUrl: string | null | undefined;
    try {
      const {data} = await signOutUser();
      auth0LogoutUrl = data?.signOutUser?.auth0LogoutUrl;

      // .clearStore will clear the cache but it will not re-fetch the queries like .resetStore()
      // This is to prevent the re-fetch queries from hitting the ErrorBoundary which
      // can result in flashes of "oh crumbs" before redirecting the users
      await client.clearStore();
    } finally {
      // Replace so the back button cannot return to the signed-in page
      redirect(to ?? auth0LogoutUrl ?? compilePath(ROOT_PATH, {}), {replace: true});
    }
  };

  return {signOut, isSigningOut};
};

export default useSignOut;
